import axios from 'axios';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Extracts a human-readable message from an unknown catch value.
 * Use this at every catch (error: unknown) site instead of inlining the narrowing.
 */
export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function getErrorMessageOrFallback(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (isRecord(error)) {
    const message =
      typeof error.message === 'string'
        ? error.message
        : typeof error.error === 'string'
          ? error.error
          : typeof error.code === 'string'
            ? error.code
            : null;

    return message || fallback;
  }

  return fallback;
}

export function getApiErrorData(error: unknown): unknown {
  if (axios.isAxiosError(error)) {
    return error.response?.data;
  }

  if (isRecord(error) && isRecord(error.response) && 'data' in error.response) {
    return error.response.data;
  }

  return undefined;
}

export function getApiErrorRecord(
  error: unknown,
): Record<string, unknown> | undefined {
  const data = getApiErrorData(error);
  return isRecord(data) ? data : undefined;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = getApiErrorRecord(error);
  if (data) {
    const apiError =
      typeof data.error === 'string'
        ? data.error
        : typeof data.message === 'string'
          ? data.message
          : null;

    if (apiError) {
      return apiError;
    }
  }

  return getErrorMessageOrFallback(error, fallback);
}

export function getErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }

  if (isRecord(error)) {
    if (typeof error.status === 'number') {
      return error.status;
    }

    if (isRecord(error.response) && typeof error.response.status === 'number') {
      return error.response.status;
    }
  }

  return undefined;
}

export function hasErrorCode(error: unknown, code: string): boolean {
  if (axios.isAxiosError(error) && error.code === code) {
    return true;
  }

  if (error instanceof Error && 'code' in error) {
    return typeof error.code === 'string' && error.code === code;
  }

  if (isRecord(error) && typeof error.code === 'string') {
    return error.code === code;
  }

  const data = getApiErrorData(error);
  return isRecord(data) && typeof data.code === 'string' && data.code === code;
}
